import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../hooks/useAuth';
import { functions } from '../firebase/config';
import Layout from '../components/layout/Layout';

/**
 * Query params que Mercado Pago puede enviar al redirigir (preapproval):
 * - preapproval_id: ID de la suscripción
 * - status: authorized | pending | cancelled (si MP lo envía)
 * - payment_id, payment_status: a veces en redirects de pago
 */
const StarIcon = () => (
  <svg className="w-8 h-8 text-amber-500 dark:text-amber-400" fill="currentColor" viewBox="0 0 20 20">
    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
  </svg>
);

const POLL_INTERVAL_MS = 2500;
const POLL_ATTEMPTS = 12;

const PremiumReturnPage = () => {
  const [searchParams] = useSearchParams();
  const { userData, currentUser } = useAuth();
  const profilePath = `/profile/${userData?.id || currentUser?.uid || 'me'}`;
  const [resolved, setResolved] = useState(false);
  const [pollCount, setPollCount] = useState(0);

  const status = searchParams.get('status') || '';
  const preapprovalId = searchParams.get('preapproval_id') || searchParams.get('preapproval-id') || '';
  const paymentStatus = searchParams.get('payment_status') || '';

  const isCancelledOrError =
    status === 'cancelled' ||
    status === 'rejected' ||
    status === 'failure' ||
    paymentStatus === 'rejected' ||
    paymentStatus === 'cancelled';
  const isPending = status === 'pending' || paymentStatus === 'pending' || paymentStatus === 'in_process';
  const hasPreapprovalId = Boolean(preapprovalId);

  const isPremium = userData?.isPremium === true;

  useEffect(() => {
    if (!hasPreapprovalId && !isCancelledOrError && !isPending) {
      setResolved(true);
      return;
    }
    if (isCancelledOrError || isPending) {
      setResolved(true);
      return;
    }
    if (isPremium) {
      setResolved(true);
      return;
    }
  }, [hasPreapprovalId, isCancelledOrError, isPending, isPremium]);

  useEffect(() => {
    if (!hasPreapprovalId || isCancelledOrError || isPending || isPremium) return;
    const t = setInterval(() => {
      setPollCount((c) => {
        if (c >= POLL_ATTEMPTS) {
          clearInterval(t);
          setResolved(true);
          return c;
        }
        return c + 1;
      });
    }, POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [hasPreapprovalId, isCancelledOrError, isPending, isPremium]);

  useEffect(() => {
    if (pollCount === 0) return;
    let cancelled = false;
    const getPaymentInfo = httpsCallable(functions, 'getPaymentInfo');
    getPaymentInfo({})
      .then(({ data }) => {
        if (cancelled) return;
        if (data?.data?.isPremium) setResolved(true);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [pollCount]);

  if (isCancelledOrError) {
    return (
      <Layout>
        <section className="container-custom max-w-lg py-16 text-center">
          <div className="p-4 rounded-2xl border-2 border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
            <p className="text-lg font-semibold text-amber-800 dark:text-amber-200 mb-2">
              No se completó el pago
            </p>
            <p className="text-sm text-amber-700 dark:text-amber-300 mb-6">
              Podés intentar de nuevo cuando quieras. Si el problema continúa, revisá que tengas fondos suficientes o probá otro método de pago.
            </p>
            <Link
              to="/premium"
              className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg font-semibold bg-amber-600 hover:bg-amber-700 text-white transition-colors"
            >
              Volver a Premium
            </Link>
          </div>
        </section>
      </Layout>
    );
  }

  if (isPending) {
    return (
      <Layout>
        <section className="container-custom max-w-lg py-16 text-center">
          <div className="p-6 rounded-2xl border-2 border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20">
            <p className="text-lg font-semibold text-blue-800 dark:text-blue-200 mb-2">
              Pago pendiente
            </p>
            <p className="text-sm text-blue-700 dark:text-blue-300 mb-6">
              Tu pago está en proceso. Te avisaremos cuando se acredite y tu suscripción Premium se active.
            </p>
            <Link
              to="/premium"
              className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg font-semibold bg-blue-600 hover:bg-blue-700 text-white transition-colors"
            >
              Ir a Premium
            </Link>
          </div>
        </section>
      </Layout>
    );
  }

  if (hasPreapprovalId && isPremium) {
    return (
      <Layout>
        <section className="container-custom max-w-lg py-16 text-center">
          <div className="p-6 rounded-2xl border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-green-600 rounded-xl">
                <svg className="w-6 h-6 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <p className="text-lg font-semibold text-green-800 dark:text-green-200 mb-2">
              ¡Ya sos Premium!
            </p>
            <p className="text-sm text-green-700 dark:text-green-300 mb-6">
              Tu suscripción está activa. Disfrutá de todos los beneficios.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/premium"
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg font-semibold bg-green-600 hover:bg-green-700 text-white transition-colors"
              >
                Ver Premium
              </Link>
              <Link
                to={profilePath}
                className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg font-semibold bg-white dark:bg-slate-800 border-2 border-green-600 text-green-700 dark:text-green-300 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
              >
                Ver mi perfil
              </Link>
            </div>
          </div>
        </section>
      </Layout>
    );
  }

  if (hasPreapprovalId && !resolved) {
    return (
      <Layout>
        <section className="container-custom max-w-lg py-16 text-center">
          <div className="p-6 rounded-2xl border-2 border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-indigo-600 rounded-xl animate-pulse">
                <StarIcon />
              </div>
            </div>
            <p className="text-lg font-semibold text-indigo-800 dark:text-indigo-200 mb-2">
              Procesando tu pago…
            </p>
            <p className="text-sm text-indigo-700 dark:text-indigo-300">
              En unos segundos tu suscripción Premium se activará. No cierres esta página.
            </p>
          </div>
        </section>
      </Layout>
    );
  }

  if (hasPreapprovalId && resolved && !isPremium) {
    return (
      <Layout>
        <section className="container-custom max-w-lg py-16 text-center">
          <div className="p-6 rounded-2xl border-2 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20">
            <p className="text-lg font-semibold text-green-800 dark:text-green-200 mb-2">
              Pago recibido
            </p>
            <p className="text-sm text-green-700 dark:text-green-300 mb-6">
              Tu suscripción se está activando. Si en unos minutos no ves el badge Premium, recargá la página o contactanos.
            </p>
            <Link
              to="/premium"
              className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg font-semibold bg-green-600 hover:bg-green-700 text-white transition-colors"
            >
              Ir a Premium
            </Link>
          </div>
        </section>
      </Layout>
    );
  }

  return (
    <Layout>
      <section className="container-custom max-w-lg py-16 text-center">
        <div className="p-6 rounded-2xl border-2 border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800">
          <p className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
            Volviste de Mercado Pago
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
            Si no completaste el pago, podés intentar de nuevo desde la página Premium.
          </p>
          <Link
            to="/premium"
            className="inline-flex items-center justify-center px-5 py-2.5 rounded-lg font-semibold bg-indigo-600 hover:bg-indigo-700 text-white transition-colors"
          >
            Ir a Premium
          </Link>
        </div>
      </section>
    </Layout>
  );
};

export default PremiumReturnPage;
